<template>
  <main class="auth-form-page">
    <AppNavBar :border="false" />

    <section class="auth-form-head">
      <h1>{{ step === 1 ? pageTitle : t('auth.enterVerificationCode') }}</h1>
      <p v-if="step === 1">{{ t('auth.emailAccountTip') }}</p>
      <p v-else>
        {{ t('auth.codeSentTo') }}
        <span>{{ email }}</span>
        {{ t('auth.codeValid') }}
      </p>
    </section>

    <section v-if="step === 1" class="auth-form-section">
      <label class="auth-form-label">{{ t('auth.emailAddress') }}</label>
      <van-field
        v-model="email"
        class="auth-form-field"
        clearable
        type="email"
        :placeholder="t('auth.enterEmailAddress')"
      />
      <AuthThemeButton
        block
        class="auth-submit-btn"
        :disabled="!emailValid"
        @click="goNextStep"
      >
        {{ submitLabel }}
      </AuthThemeButton>
    </section>

    <section v-else class="auth-form-section">
      <van-field
        v-model="code"
        class="auth-form-field"
        clearable
        maxlength="8"
        :placeholder="t('auth.emailCode')"
      >
        <template #button>
          <button
            type="button"
            class="auth-code-btn"
            :disabled="sending || countdown > 0"
            @click="sendCode"
          >
            {{ countdown > 0 ? `${t('auth.resend')}${countdown}s` : t('auth.getCode') }}
          </button>
        </template>
      </van-field>
      <AuthThemeButton
        block
        class="auth-submit-btn"
        :disabled="!code.trim()"
        :loading="verifying"
        @click="verifyCode"
      >
        {{ t('auth.verify') }}
      </AuthThemeButton>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { showFailToast, showToast } from 'vant';
import { showPassedToast } from '@/utils/toast';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { emailCheck, emailLogin, emailSend, startWebxBinding } from '@/api/auth';
import AuthThemeButton from '@/view/auth/components/AuthThemeButton.vue';
import { getDeviceId } from '@/utils/device';
import { getRouteState, navigateWithState } from '@/utils/route-state';
import {
  getRecoverableMnemonic,
  getWalletSignerSecret,
  saveWalletMnemonic,
  saveWalletPassword,
  saveWalletSignerSecret
} from '@/utils/walletSession';
import { useProfileStore } from '@/stores/profile';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const profileStore = useProfileStore();
const pageState = computed(() => getRouteState(route, 'auth-email', {}));

const step = ref(1);
const email = ref('');
const code = ref('');
const sending = ref(false);
const verifying = ref(false);
const countdown = ref(0);
let timer = null;

const isLogin = computed(() => pageState.value.type === 'login');
const emailActionType = computed(() => (isLogin.value ? 'LOGIN' : 'REGISTER'));
const emailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value));
const submitLabel = computed(() => (isLogin.value ? t('auth.login') : t('auth.register')));
const pageTitle = computed(() => (
  isLogin.value ? t('auth.inputEmailAddress') : t('auth.addEmailAddress')
));

function generateRandomName() {
  const letters = 'abcdefghijklmnopqrstuvwxyz';
  const length = Math.floor(Math.random() * 6) + 3;
  let result = String.fromCharCode(65 + Math.floor(Math.random() * 26));
  for (let i = 1; i < length; i += 1) {
    result += letters[Math.floor(Math.random() * letters.length)];
  }
  return result;
}

function normalizeAddress(value) {
  return String(value || '').trim().toLowerCase();
}

function startCountdown() {
  countdown.value = 60;
  window.clearInterval(timer);
  timer = window.setInterval(() => {
    countdown.value -= 1;
    if (countdown.value <= 0) {
      window.clearInterval(timer);
      timer = null;
    }
  }, 1000);
}

function goNextStep() {
  if (!emailValid.value) {
    showFailToast(t('auth.enterEmail'));
    return;
  }
  step.value = 2;
}

async function sendCode() {
  if (sending.value || countdown.value > 0) {
    return;
  }
  if (!emailValid.value) {
    showFailToast(t('auth.enterEmail'));
    return;
  }

  sending.value = true;
  try {
    const result = await emailSend({
      token: getDeviceId(),
      email: email.value.trim(),
      type: emailActionType.value
    });
    if (!result && result !== true) {
      return;
    }
    startCountdown();
    showPassedToast(t('auth.codeSent'));
  } finally {
    sending.value = false;
  }
}

async function verifyCode() {
  const verifyCodeValue = code.value.trim();
  if (!verifyCodeValue) {
    showFailToast(t('auth.codeRequired'));
    return;
  }

  verifying.value = true;
  try {
    const checkResult = await emailCheck({
      email: email.value.trim(),
      type: emailActionType.value,
      token: getDeviceId(),
      code: verifyCodeValue
    });
    if (!checkResult && checkResult !== true) {
      return;
    }

    if (isLogin.value) {
      const wallet = await emailLogin({
        email: email.value.trim(),
        token: getDeviceId(),
        code: verifyCodeValue
      });
      if (!wallet?.address) {
        return;
      }
      const accountName = wallet?.nickName || generateRandomName();
      const currentAddress = normalizeAddress(wallet.address);
      const existingByAddress = profileStore.accounts.find((item) => {
        const candidates = [
          item?.address,
          item?.ethAddress,
          item?.tronAddress,
          item?.bitcoinAddress
        ].map(normalizeAddress).filter(Boolean);
        return candidates.includes(currentAddress);
      });
      const currentEmail = String(wallet?.email || email.value || '').trim().toLowerCase();
      const existingByEmail = currentEmail
        ? profileStore.accounts.find((item) => String(item?.email || '').trim().toLowerCase() === currentEmail)
        : null;
      const existingAccount = existingByAddress || existingByEmail || null;
      const existingAddress = String(existingAccount?.address || existingAccount?.ethAddress || '').trim();
      const existingMnemonic = existingAddress ? getRecoverableMnemonic(existingAddress) : '';
      const existingSigner = existingAddress ? getWalletSignerSecret(existingAddress) : null;

      if (wallet?.mnemonic) {
        saveWalletMnemonic(wallet.address, wallet.mnemonic);
        saveWalletSignerSecret(wallet.address, wallet.mnemonic, 'mnemonic');
      } else if (wallet?.privateKey) {
        saveWalletSignerSecret(wallet.address, wallet.privateKey, 'privateKey');
      } else if (existingMnemonic) {
        saveWalletMnemonic(wallet.address, existingMnemonic);
        saveWalletSignerSecret(wallet.address, existingMnemonic, 'mnemonic');
      } else if (existingSigner?.secret) {
        saveWalletSignerSecret(wallet.address, existingSigner.secret, existingSigner.type);
      }

      await profileStore.setCurrentAccount({
        ...(existingAccount || {}),
        ...wallet,
        address: wallet.address,
        name: accountName,
        email: wallet.email || email.value.trim(),
        walletWay: 'ByEmail',
        type: '0',
        isBackup: false
      });
      profileStore.setWalletType('loginEmail');
      profileStore.setFirstVisit(true);
      if (wallet?.token) {
        profileStore.setToken(wallet.token);
      }
      if (wallet?.address && wallet?.payPwd) {
        saveWalletPassword(wallet.address, wallet.payPwd);
      }
      try {
        await startWebxBinding();
      } catch (error) {
        await router.replace('/home/index');
      }
      return;
    }

    navigateWithState(router, '/auth/create-wallet', {
      namespace: 'auth-create-wallet',
      payload: {
        type: 'new',
        email: email.value.trim(),
        code: verifyCodeValue,
        token: getDeviceId()
      }
    });
  } finally {
    verifying.value = false;
  }
}

onBeforeUnmount(() => {
  window.clearInterval(timer);
});
</script>

<style scoped lang="scss">
@use '@/styles/auth-form.scss';
</style>
